import React from 'react';
import { useTranslation } from 'react-i18next';
import rabiStudentImg from '../assets/webp/my-parasha-header.webp';
import i18n from '../i18n';

const BLUE_ACCENT = '#3B7BF4';
const TEXT_DARK = '#1A1A2E';
const TEXT_SECONDARY = '#6B7280';
const BORDER_LIGHT = '#D1D5DB';

export type MyParashaSectionProps = {
  parashaLabel: string;
  progressPercent: number;
  masteredCount: number;
  practiceMinutes: number;
  direction: 'rtl' | 'ltr';
  targetDate?: Date | null;
  traditionKey?: string | null;
  haftaraLabel?: string | null;
  barMitzvaDateLabel?: string | null;
  hebrewDateLabel?: string | null;
};

function calcCountdown(target: Date): { months: number; weeks: number; days: number } {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const totalDays = Math.max(0, Math.floor((target.getTime() - now.getTime()) / 86400000));
  const months = Math.floor(totalDays / 30);
  const remaining = totalDays - months * 30;
  const weeks = Math.floor(remaining / 7);
  const days = remaining % 7;
  return { months, weeks, days };
}

const OVERLAP = 30;

const MyParashaSection: React.FC<MyParashaSectionProps> = ({
  parashaLabel,
  progressPercent,
  masteredCount,
  practiceMinutes,
  direction,
  targetDate,
  traditionKey,
  haftaraLabel,
  barMitzvaDateLabel,
  hebrewDateLabel,
}) => {
  const { t } = useTranslation();

  const countdown = targetDate ? calcCountdown(targetDate) : null;
  const countdownSquares = countdown
    ? [
        { value: countdown.months, label: t('countdown_months', 'חודשים') },
        { value: countdown.weeks, label: t('countdown_weeks', 'שבועות') },
        { value: countdown.days, label: t('countdown_days', 'ימים') },
      ]
    : null;

  return (
    <section
      style={{
        background: '#ffffff',
        borderRadius: 26,
        overflow: 'visible',
        boxShadow: '0 20px 36px rgba(0,0,0,0.14)',
        border: '1px solid #e5e7eb',
        position: 'relative',
      }}
    >
      {/* Blue header */}
      <div
        style={{
          background: BLUE_ACCENT,
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          position: 'relative',
          overflow: 'visible',
          // Taller when the countdown strip is shown: the squares intrude ~28px
          // into the header bottom, so give the text room above them.
          height: countdownSquares ? 145 : 115,
          paddingBottom: 0,
        }}
      >
        {/*Illustration — left*/}
        <img
          alt=""
          src={rabiStudentImg}
          style={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            height: 200,
            pointerEvents: 'none',
          }}
        />

        {/* Center text */}
        <div
          style={{
            position: 'relative',
            zIndex: 10,
            height: '100%',
            marginRight: 30,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            width: '50%',
            justifyContent: 'center',
            paddingTop: 5,
            paddingBottom: countdownSquares ? 28 : 0,
            flexDirection: 'column',
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: 0.5 }}>
            — {t('your_parasha_label', 'הפרשה שלך')} —
          </div>
          <div style={{ lineHeight: '1.1', fontSize: 34, fontWeight: 700 }}>{parashaLabel}</div>
          {haftaraLabel && (
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: 0.5 }}>
                — {t('haftara_prefix', 'הפטרה')} —
              </div>
              <div
                style={{
                  maxWidth: 200,
                  whiteSpace: 'nowrap',
                  overflow: 'HIDDEN',
                  textOverflow: 'ellipsis',
                  lineHeight: '1.1',
                  fontSize: 20,
                  fontWeight: 700,
                }}
              >
                {haftaraLabel}
              </div>
            </div>
          )}
        </div>

        {/* Countdown strip — overlaps the blue/white boundary */}
        {countdownSquares && (
          <div
            style={{
              position: 'absolute',
              bottom: -OVERLAP,
              left: 0,
              right: 0,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              zIndex: 10,
              direction: 'rtl',
              paddingRight: 20,
            }}
            aria-label={t('countdown_aria', 'Time until parasha') as string}
          >
            {/* Countdown squares — first child in RTL = right side */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                flexShrink: 0,
                direction: 'ltr',
              }}
            >
              {countdownSquares.map((sq, i) => (
                <React.Fragment key={i}>
                  {i > 0 && (
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: TEXT_DARK,
                        opacity: 0.4,
                        lineHeight: 1,
                      }}
                    >
                      ·
                    </div>
                  )}
                  <div
                    style={{
                      background: '#F5C842',
                      borderRadius: 10,
                      padding: '4px 6px',
                      minWidth: 54,
                      height: 58,
                      textAlign: 'center',
                      boxShadow: '0 4px 12px rgba(245,200,66,0.4)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.1, color: TEXT_DARK }}
                    >
                      {sq.value}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: TEXT_DARK }}>
                      {sq.label}
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>

            {/* Date label — flex: 1 fills the left side behind the illustration */}
            {(hebrewDateLabel || barMitzvaDateLabel) && (
              <div
                style={{
                  flex: 1,
                  textAlign: 'center',
                  direction: 'rtl',
                  marginTop: 13,
                  background: '#DDE9F9',
                }}
              >
                {i18n.language === 'he' && hebrewDateLabel && (
                  <div style={{ fontSize: 11, fontWeight: 500, color: TEXT_DARK, lineHeight: 1.3 }}>
                    {hebrewDateLabel}
                  </div>
                )}
                {i18n.language !== 'he' && barMitzvaDateLabel && (
                  <div style={{ fontSize: 10, color: TEXT_SECONDARY, lineHeight: 1.3 }}>
                    {barMitzvaDateLabel}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stats section */}
      <div
        style={{
          padding: `${countdownSquares ? OVERLAP + 18 : 16}px 18px 18px`,
          borderBottomLeftRadius: 20,
          borderBottomRightRadius: 20,
        }}
      >
        {/* Mastery level row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
            color: TEXT_DARK,
            direction,
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            {t('user_portal_mastery_level', 'רמת שליטה')}
          </div>
          <div style={{ color: BLUE_ACCENT, fontWeight: 700, fontSize: 18 }}>
            {progressPercent}%
          </div>
        </div>

        {/* Progress bar */}
        <div
          style={{
            width: '100%',
            height: 8,
            borderRadius: 999,
            background: 'rgba(59, 123, 244, 0.15)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${progressPercent}%`,
              height: '100%',
              background: BLUE_ACCENT,
              borderRadius: 999,
              transition: 'width 0.25s ease',
            }}
          />
        </div>

        {/* Stat cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
          <div
            style={{
              background: '#f9fafb',
              borderRadius: 14,
              padding: '4px 10px',
              border: `1px solid ${BORDER_LIGHT}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              direction,
            }}
          >
            <div
              style={{
                display: 'flex',
                lineHeight: 1.3,
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <span style={{ fontSize: 13, color: TEXT_SECONDARY }}>
                {t('user_portal_mastered', 'פסוקים בשליטה')}
              </span>
              <span style={{ fontSize: 22, fontWeight: 700, color: TEXT_DARK }}>
                {masteredCount}
              </span>
            </div>
          </div>

          <div
            style={{
              background: '#f9fafb',
              borderRadius: 14,
              padding: '4px 10px',
              border: `1px solid ${BORDER_LIGHT}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              direction,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                lineHeight: 1.3,
                alignItems: 'center',
                width: '100%',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ fontSize: 13, color: TEXT_SECONDARY }}>
                {t('user_portal_practice_today', 'תרגול היום')}
              </span>
              <span style={{ fontSize: 22, fontWeight: 700, color: TEXT_DARK }}>
                {t('user_portal_minutes_short', "{{count}} דק'", { count: practiceMinutes })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MyParashaSection;
